import { useState } from 'react';
import { SearchableConversationFilterInput } from 'generated-graphql/types';
import { useExportAllCampaignsDataV2LazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { ToastType, useToast } from 'services/toast';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';

const UNFILTERED_EXPORT_KEY = 'unfiltered';

type Props = {
  // The conversations list's filter, or undefined for the whole account.
  filter?: SearchableConversationFilterInput;
};

/**
 * The Export data button. Emails the whole account, or with a filter, only
 * the conversations that filter matches.
 *
 * "Export sent" belongs to each file requested, not to the session: a filter
 * not yet exported offers the button again, and any filter already exported
 * keeps reading "Export sent", so returning to it cannot request it twice.
 */
export const useExportCampaignsData = ({ filter }: Props) => {
  const { showToast } = useToast();
  const { track } = useTracking();

  const [exportAllCampaignsDataV2, { loading: isExporting }] =
    useExportAllCampaignsDataV2LazyQuery({
      fetchPolicy: 'network-only'
    });

  // AWSJSON travels as a string. Undefined drops out of the request, so an
  // unfiltered export is the request it always was.
  const serialisedFilter = filter ? JSON.stringify(filter) : undefined;
  const exportKey = serialisedFilter ?? UNFILTERED_EXPORT_KEY;

  const [exportedKeys, setExportedKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const showFailure = () =>
    showToast({
      message: 'Sorry we could not export the data',
      type: ToastType.Error
    });

  const exportData = async () => {
    try {
      track(TrackingEventName.CampaignsListExport);

      const { data, error } = await exportAllCampaignsDataV2({
        variables: { filter: serialisedFilter }
      });

      if (!data?.exportAllCampaignsDataV2?.message || error) {
        showFailure();
        return;
      }

      setExportedKeys((previous) => new Set(previous).add(exportKey));
      showToast({
        message:
          'Data exported successfully. You will receive an email with the download link.',
        type: ToastType.Success
      });
    } catch {
      showFailure();
    }
  };

  return {
    exportData,
    isExporting,
    hasExported: exportedKeys.has(exportKey)
  };
};
